import React from 'react';
import type { PlanContext } from '../../api/plan';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import {
  FILTERS,
  fill,
  groupByTheme,
  type StateFilter,
} from '../../today/todayViewModel';
import ProgressMarks from './ProgressMarks';
import styles from './Browse.module.css';

export const BROWSE_HEADING_ID = 'browse-heading';

interface BrowsePanelProps {
  situations: PlanContext[];
  query: string;
  filter: StateFilter;
  onQuery: (query: string) => void;
  onFilter: (filter: StateFilter) => void;
  onOpen: (context: PlanContext) => void;
  onBack: () => void;
}

const pillFor = (context: PlanContext): { className: string; text: string } => {
  switch (context.state) {
    case 'passed': {
      const date = context.passed_at
        ? new Date(context.passed_at).toLocaleDateString()
        : null;
      return {
        className: styles.pillOk,
        text: date
          ? fill(INTERFACE_TEXT.tdStatePassedOn, { date })
          : INTERFACE_TEXT.tdStatePassed,
      };
    }
    case 'in_progress':
      return {
        className: styles.pillProgress,
        text: INTERFACE_TEXT.tdStateInProgress,
      };
    case 'locked':
      return {
        className: styles.pillLocked,
        text: INTERFACE_TEXT.tdStateLocked,
      };
    default:
      return { className: styles.pillOpen, text: INTERFACE_TEXT.tdStateOpen };
  }
};

const subline = (context: PlanContext): string => {
  if (context.state !== 'locked') return context.blurb ?? '';
  const names = context.missing_prerequisites
    .map((p) => p.context_name)
    .join(', ');
  return names
    ? fill(INTERFACE_TEXT.tdOpensWhenPass, { names })
    : INTERFACE_TEXT.tdLockedGeneric;
};

/** Every situation grouped by theme, with search and state filters (BR-049). */
export const BrowsePanel: React.FC<BrowsePanelProps> = ({
  situations,
  query,
  filter,
  onQuery,
  onFilter,
  onOpen,
  onBack,
}) => {
  const groups = groupByTheme(situations, query, filter);
  const emptyText = query.trim()
    ? fill(INTERFACE_TEXT.tdNoMatch, { q: query.trim() })
    : INTERFACE_TEXT.tdNoMatchFilter;
  return (
    <div className={styles.panel}>
      <div className={styles.head}>
        <button type="button" className={styles.back} onClick={onBack}>
          {INTERFACE_TEXT.tdBackToToday}
        </button>
        <h2 id={BROWSE_HEADING_ID} tabIndex={-1} className={styles.title}>
          {INTERFACE_TEXT.tdAllSituations}
        </h2>
      </div>
      <input
        className={styles.search}
        type="search"
        aria-label={INTERFACE_TEXT.tdSearchLabel}
        placeholder={fill(INTERFACE_TEXT.tdSearchPlaceholder, {
          n: situations.length,
        })}
        value={query}
        onChange={(e) => onQuery(e.target.value)}
      />
      <div
        className={styles.filters}
        role="group"
        aria-label={INTERFACE_TEXT.tdFilterLabel}
      >
        {FILTERS.map(({ value, label }) => (
          <button
            key={value}
            type="button"
            aria-pressed={filter === value}
            onClick={() => onFilter(value)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className={styles.list}>
        {groups.length === 0 && <p className={styles.empty}>{emptyText}</p>}
        {groups.map((group) => (
          <section key={group.theme} className={styles.theme}>
            <h3 className={styles.themeHead}>
              {group.theme}
              <span>
                {group.situations.length === 1
                  ? INTERFACE_TEXT.tdThemeCountOne
                  : fill(INTERFACE_TEXT.tdThemeCountMany, {
                      n: group.situations.length,
                    })}
              </span>
            </h3>
            {group.situations.map((context) => {
              const pill = pillFor(context);
              return (
                <button
                  key={context.context_id}
                  type="button"
                  className={`${styles.row} ${context.state === 'locked' ? styles.rowLocked : ''}`}
                  onClick={() => onOpen(context)}
                >
                  <b>{context.context_name}</b>
                  <small>{subline(context)}</small>
                  <span className={styles.status}>
                    <span className={`${styles.pill} ${pill.className}`}>
                      {pill.text}
                    </span>
                    <ProgressMarks context={context} />
                  </span>
                </button>
              );
            })}
          </section>
        ))}
      </div>
    </div>
  );
};

export default BrowsePanel;
